import { AlertCircle, Lock, Minus, Pause, Play, PlayCircle, Plus, Snowflake } from 'lucide-react';
import { Button } from '../ui/Button';
import type { ActividadEstado } from '../../lib/sesion';

/**
 * QUÉ HACER AHORA — una sola acción principal grande por estado (fullWidth en
 * móvil), con las secundarias compactas al lado. Muere el racimo de botones
 * iguales:
 *  - preparada → «Abrir esta actividad»
 *  - abierta   → «Cerrar y congelar el acta» (+ Pausar y +1 minuto secundarias)
 *  - pausada   → «Reanudar» (+1 minuto y cerrar secundarias)
 *  - cerrada   → «Acta congelada · N aportes»
 * Las mutaciones viven en la página; acá solo la presentación.
 */
export function AccionPrincipal({
  estado,
  puedeConducir,
  totalAportes,
  error,
  abriendo,
  cerrando,
  pausando,
  reanudando,
  sumandoTiempo,
  onAbrir,
  onCerrar,
  onPausar,
  onReanudar,
  onSumarMinuto,
  onRestarMinuto,
}: {
  estado: ActividadEstado;
  /** Mismo gate de conducción que la agenda; el fino lo pone el backend. */
  puedeConducir: boolean;
  totalAportes: number;
  /** Detail humano de la última mutación fallida (patrón accionError). */
  error: string | null;
  abriendo: boolean;
  cerrando: boolean;
  pausando: boolean;
  reanudando: boolean;
  sumandoTiempo: boolean;
  onAbrir: () => void;
  onCerrar: () => void;
  onPausar: () => void;
  onReanudar: () => void;
  onSumarMinuto: () => void;
  /** D2 · timeboxing estricto: acortar también es conducir el tiempo. */
  onRestarMinuto: () => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      {estado === 'preparada' && (
        <Button
          variant="primary"
          size="lg"
          className="w-full sm:w-auto sm:self-start"
          disabled={!puedeConducir}
          loading={abriendo}
          onClick={onAbrir}
          leadingIcon={<PlayCircle className="h-5 w-5" aria-hidden="true" />}
        >
          Abrir esta actividad
        </Button>
      )}

      {estado === 'abierta' && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button
            variant="primary"
            size="lg"
            className="w-full sm:w-auto"
            disabled={!puedeConducir}
            loading={cerrando}
            onClick={onCerrar}
            leadingIcon={<Lock className="h-5 w-5" aria-hidden="true" />}
          >
            Cerrar y congelar el acta
          </Button>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={!puedeConducir}
              loading={pausando}
              onClick={onPausar}
              leadingIcon={<Pause className="h-4 w-4" aria-hidden="true" />}
            >
              Pausar
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={!puedeConducir}
              loading={sumandoTiempo}
              onClick={onSumarMinuto}
              leadingIcon={<Plus className="h-4 w-4" aria-hidden="true" />}
            >
              1 minuto
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={!puedeConducir}
              loading={sumandoTiempo}
              onClick={onRestarMinuto}
              aria-label="Restar 1 minuto"
              leadingIcon={<Minus className="h-4 w-4" aria-hidden="true" />}
            >
              1 minuto
            </Button>
          </div>
        </div>
      )}

      {estado === 'pausada' && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button
            variant="primary"
            size="lg"
            className="w-full sm:w-auto"
            disabled={!puedeConducir}
            loading={reanudando}
            onClick={onReanudar}
            leadingIcon={<Play className="h-5 w-5" aria-hidden="true" />}
          >
            Reanudar
          </Button>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={!puedeConducir}
              loading={sumandoTiempo}
              onClick={onSumarMinuto}
              leadingIcon={<Plus className="h-4 w-4" aria-hidden="true" />}
            >
              1 minuto
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={!puedeConducir}
              loading={sumandoTiempo}
              onClick={onRestarMinuto}
              aria-label="Restar 1 minuto"
              leadingIcon={<Minus className="h-4 w-4" aria-hidden="true" />}
            >
              1 minuto
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={!puedeConducir}
              loading={cerrando}
              onClick={onCerrar}
              leadingIcon={<Lock className="h-4 w-4" aria-hidden="true" />}
            >
              Cerrar y congelar el acta
            </Button>
          </div>
        </div>
      )}

      {estado === 'cerrada' && (
        <p className="flex items-center gap-2 font-sans text-sm text-success">
          <Snowflake className="h-4 w-4" aria-hidden="true" />
          Acta congelada · {totalAportes} aportes en el artefacto
        </p>
      )}

      {error && (
        <p className="flex items-center gap-1.5 font-sans text-sm text-error">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          {error}
        </p>
      )}
    </div>
  );
}
